/**
 * Framework Notes page — left list + right Markdown editor (explicit Save).
 * Editor v1: textarea + GFM preview (opaque body; preserves math delimiters).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  NotebookPen,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
} from 'lucide-react';
import { MarkdownRenderer } from '../components/MarkdownRenderer.js';

export interface NoteDto {
  id: string;
  owner_slug: string;
  title: string;
  body: string;
  tags: string[];
  provenance: string;
  created_at: string;
  updated_at: string;
}

export interface NoteListItem {
  id: string;
  owner_slug: string;
  title: string;
  tags: string[];
  updated_at: string;
  created_at: string;
  body_preview: string;
  body_truncated: boolean;
}

interface NotesPageProps {
  apiBase: string;
  title: string;
  navigate: (path: string) => void;
  /** Path after /notes — e.g. "/notes/uuid" → selected id */
  path: string;
  viewerSlug: string;
}

function formatWhen(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function noteIdFromPath(path: string): string | null {
  const m = path.match(/^\/notes\/([^/]+)\/?$/);
  if (!m) return null;
  const id = m[1]!;
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
  ) {
    return null;
  }
  return id;
}

export function NotesPage({
  apiBase,
  title,
  navigate,
  path,
  viewerSlug,
}: NotesPageProps) {
  const [items, setItems] = useState<NoteListItem[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const pathId = noteIdFromPath(path);
  const pathInvalid =
    path.startsWith('/notes/') && path !== '/notes' && pathId === null;

  const [selectedId, setSelectedId] = useState<string | null>(pathId);
  const [note, setNote] = useState<NoteDto | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  const dirty = useMemo(() => {
    if (!note) return false;
    return editTitle !== note.title || editBody !== note.body;
  }, [note, editTitle, editBody]);

  const loadList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await fetch(apiBase, { credentials: 'include' });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error || `list failed ${res.status}`);
      }
      const data = (await res.json()) as { items: NoteListItem[] };
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      setListError(e instanceof Error ? e.message : String(e));
      setItems([]);
    } finally {
      setListLoading(false);
    }
  }, [apiBase]);

  const loadNote = useCallback(
    async (id: string) => {
      setNoteLoading(true);
      setNoteError(null);
      setBanner(null);
      try {
        const res = await fetch(`${apiBase}/${id}`, { credentials: 'include' });
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as {
            error?: string;
            code?: string;
          };
          if (res.status === 404 || j.code === 'not_found') {
            setNote(null);
            setEditTitle('');
            setEditBody('');
            setNoteError('not_found');
            return;
          }
          throw new Error(j.error || `load failed ${res.status}`);
        }
        const data = (await res.json()) as { note: NoteDto };
        setNote(data.note);
        setEditTitle(data.note.title);
        setEditBody(data.note.body);
      } catch (e) {
        setNote(null);
        setNoteError(e instanceof Error ? e.message : String(e));
      } finally {
        setNoteLoading(false);
      }
    },
    [apiBase],
  );

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (pathInvalid) {
      setSelectedId(null);
      setNote(null);
      setBanner('invalid_note_id');
      return;
    }
    if (pathId) {
      setSelectedId(pathId);
    }
  }, [pathId, pathInvalid]);

  useEffect(() => {
    if (!selectedId) {
      setNote(null);
      setEditTitle('');
      setEditBody('');
      setNoteError(null);
      return;
    }
    void loadNote(selectedId);
  }, [selectedId, loadNote]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        n.body_preview.toLowerCase().includes(q) ||
        n.tags.some((t) => t.includes(q)),
    );
  }, [items, filter]);

  async function handleNew() {
    setBanner(null);
    try {
      const res = await fetch(apiBase, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'Untitled', body: '' }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error || `create failed ${res.status}`);
      }
      const data = (await res.json()) as { note: NoteDto };
      await loadList();
      setSelectedId(data.note.id);
      navigate(`/notes/${data.note.id}`);
    } catch (e) {
      setBanner(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleSave() {
    if (!note || !dirty) return;
    setSaving(true);
    setBanner(null);
    try {
      const res = await fetch(`${apiBase}/${note.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editTitle,
          body: editBody,
          updated_at: note.updated_at,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
          note?: NoteDto;
        };
        if (res.status === 409 && j.note) {
          setNote(j.note);
          setEditTitle(j.note.title);
          setEditBody(j.note.body);
          setBanner('conflict: note changed elsewhere — reloaded latest');
          return;
        }
        throw new Error(j.error || `save failed ${res.status}`);
      }
      const data = (await res.json()) as { note: NoteDto };
      setNote(data.note);
      setEditTitle(data.note.title);
      setEditBody(data.note.body);
      await loadList();
    } catch (e) {
      setBanner(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!note) return;
    if (!window.confirm(`Delete note “${note.title}”? This cannot be undone.`)) {
      return;
    }
    setBanner(null);
    try {
      const res = await fetch(`${apiBase}/${note.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error || `delete failed ${res.status}`);
      }
      setSelectedId(null);
      setNote(null);
      navigate('/notes');
      await loadList();
    } catch (e) {
      setBanner(e instanceof Error ? e.message : String(e));
    }
  }

  function selectNote(id: string) {
    setSelectedId(id);
    navigate(`/notes/${id}`);
  }

  return (
    <div className="flex h-[calc(100dvh-3rem)] min-h-0 w-full bg-stone-50">
      {/* Left rail */}
      <aside className="flex w-72 shrink-0 flex-col border-r border-stone-200 bg-white">
        <div className="flex items-center justify-between border-b border-stone-100 px-3 py-2.5">
          <div className="flex items-center gap-1.5 font-serif text-sm font-semibold text-stone-900">
            <NotebookPen className="h-4 w-4 text-stone-500" />
            {title}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void loadList()}
              className="rounded-lg p-1.5 text-stone-500 hover:bg-stone-100 hover:text-stone-800"
              title="Refresh"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => void handleNew()}
              className="inline-flex items-center gap-1 rounded-lg bg-stone-900 px-2 py-1 text-[11px] font-medium text-stone-50 hover:bg-stone-800"
            >
              <Plus className="h-3 w-3" />
              New
            </button>
          </div>
        </div>
        <div className="border-b border-stone-100 px-2 py-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-stone-200 bg-stone-50 px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-stone-400" />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Search notes…"
              className="w-full bg-transparent text-xs text-stone-800 outline-none placeholder:text-stone-400"
            />
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {listLoading && (
            <div className="flex justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
            </div>
          )}
          {listError && (
            <p className="px-3 py-4 text-xs text-rose-700">{listError}</p>
          )}
          {!listLoading && !listError && filtered.length === 0 && (
            <p className="px-3 py-6 text-center text-xs text-stone-500">
              No notes yet. Create one to get started.
            </p>
          )}
          {filtered.map((n) => {
            const active = n.id === selectedId;
            return (
              <button
                key={n.id}
                type="button"
                onClick={() => selectNote(n.id)}
                className={`block w-full border-b border-stone-50 px-3 py-2.5 text-left transition ${
                  active
                    ? 'bg-stone-100'
                    : 'hover:bg-stone-50'
                }`}
              >
                <div className="truncate text-xs font-medium text-stone-900">
                  {n.title}
                </div>
                <div className="mt-0.5 truncate text-[10px] text-stone-500">
                  {formatWhen(n.updated_at) ?? n.updated_at}
                </div>
                {n.tags.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {n.tags.slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="rounded bg-stone-100 px-1 py-0.5 text-[9px] text-stone-600"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </aside>

      {/* Right editor */}
      <section className="flex min-w-0 flex-1 flex-col">
        {(banner || pathInvalid) && (
          <div className="border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-900">
            {pathInvalid ? 'invalid_note_id' : banner}
          </div>
        )}
        {!selectedId && !pathInvalid && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-stone-500">
            <NotebookPen className="h-8 w-8 text-stone-300" />
            <p>Select a note or create a new one.</p>
            <p className="text-xs text-stone-400">Signed in as {viewerSlug}</p>
          </div>
        )}
        {selectedId && noteLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-stone-400" />
          </div>
        )}
        {selectedId && !noteLoading && noteError && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 text-sm text-rose-700">
            <p>{noteError}</p>
            <button
              type="button"
              className="text-stone-900 underline"
              onClick={() => {
                setSelectedId(null);
                navigate('/notes');
              }}
            >
              Back to list
            </button>
          </div>
        )}
        {selectedId && !noteLoading && note && (
          <>
            <div className="flex items-center gap-2 border-b border-stone-200 bg-white px-4 py-2">
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="min-w-0 flex-1 font-serif text-lg font-semibold text-stone-900 outline-none placeholder:text-stone-400"
                placeholder="Title"
              />
              <button
                type="button"
                disabled={!dirty || saving}
                onClick={() => void handleSave()}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition ${
                  dirty && !saving
                    ? 'bg-stone-900 text-stone-50 hover:bg-stone-800'
                    : 'cursor-not-allowed bg-stone-100 text-stone-400'
                }`}
              >
                {saving ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Save className="h-3.5 w-3.5" />
                )}
                Save
              </button>
              <button
                type="button"
                onClick={() => void handleDelete()}
                className="inline-flex items-center gap-1 rounded-xl px-2 py-1.5 text-xs text-rose-700 hover:bg-rose-50"
                title="Delete note"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {dirty && (
              <div className="border-b border-amber-50 bg-amber-50/50 px-4 py-1 text-[11px] text-amber-800">
                Unsaved changes
              </div>
            )}
            <div className="grid min-h-0 flex-1 grid-cols-1 divide-y divide-stone-200 lg:grid-cols-2 lg:divide-x lg:divide-y-0">
              <textarea
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="min-h-[40vh] w-full resize-none bg-white p-4 font-mono text-sm leading-relaxed text-stone-800 outline-none"
                placeholder="Write Markdown…"
                spellCheck
              />
              <div className="min-h-0 overflow-y-auto bg-stone-50/80 p-4">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-stone-400">
                  Preview
                </div>
                {editBody.trim() ? (
                  <div className="prose prose-stone prose-sm max-w-none">
                    <MarkdownRenderer text={editBody} viewerSlug={viewerSlug} />
                  </div>
                ) : (
                  <p className="text-xs text-stone-400">Empty note</p>
                )}
              </div>
            </div>
            <div className="border-t border-stone-100 px-4 py-1.5 text-[10px] text-stone-400">
              Updated {formatWhen(note.updated_at) ?? note.updated_at}
              {note.tags.length > 0 ? ` · ${note.tags.join(', ')}` : ''}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
